import { ApiError } from "../api/unwrap";
import type { ManifestLayer } from "../types/contract";
import type { TargetSet } from "../stores/targetSamplingStore";
import { reportSwallowed } from "./reportSwallowed";

// Confirm copy for a server design delete. Lab results stay as a soil sample
// layer. A work order that cites the design stays open. The copy lives here
// so that choice can change in one place.
export const TARGET_LAYER_DELETE_CONFIRM = {
  button: "Delete?",
  keepsResults:
    "Lab results attached to this layer will be kept as a soil sample layer",
} as const;

// specsFor: `target-points-*` → numbered amber dots (shared with the AutoMap point look)
// `target-zones-*` → the zone fill (shared with the `zones` layer look).
export function buildTargetLayers(sets: TargetSet[]): ManifestLayer[] {
  return sets.map((s) => ({
    id: `target-${s.kind}-${s.id}`,
    kind: "geojson",
    title: s.name,
    inline: s.fc,
    default_visible: false,
    default_opacity: s.kind === "zones" ? 0.45 : 1,
    order: 205,
    content_version: s.createdAt,
    is_stale: false,
  }));
}

// Every other saved zone design — a version saved from another device, a version they never
// displace what the user is working with — had no presence in the sidebar and no way to receive
// results. The server is the truth here: SS8 lists every zone version as a `zones:<id>` event and
// Z1b serves any version's geometry, so the folder is built from those.

export interface ZoneVersionEvent {
  event_id: string; // `zones:<zone_version_id>`
  name: string;
  count: number;
  has_results: boolean;
  created_at: string;
}
export interface ZoneVersionGeom {
  is_active?: boolean;
  geojson?: GeoJSON.FeatureCollection | null;
}
export interface ServerZoneRow {
  layerId: string;
  versionId: string;
  eventId: string;
  name: string;
  count: number;
  hasResults: boolean;
  isActive: boolean;
}
export interface ServerZoneLayers {
  layers: ManifestLayer[];
  /* * by layer id — the folder reads badges (active / results) and routes deletes through this */
  meta: Map<string, ServerZoneRow>;
  /*
   *
   * Every LOADED version's polygons by VERSION ID — including the active one, which emits no layer of
   * its own (it rides the manifest `zones` row) and so cannot be found in `layers`. Zone-attached lab
   * results are drawn as their zone (`lib/soilLayers`), and the version a reading belongs to is named
   * by its own `sample_event_id`, not by whichever version happens to be active.
   */
  fcByVersion: Map<string, GeoJSON.FeatureCollection>;
}

export const ZONES_EVENT_PREFIX = "zones:";
export function zoneVersionIdOf(eventId: string): string | null {
  return eventId.startsWith(ZONES_EVENT_PREFIX)
    ? eventId.slice(ZONES_EVENT_PREFIX.length)
    : null;
}

// Server zone versions carry results on the row. A client points set answers
// through its sample event (`points:<set_id>`), the same list the old/new split reads.
export function targetLayerHasResults(
  layer: { id?: string },
  meta: ReadonlyMap<string, { hasResults: boolean }>,
  events: { event_id: string; has_results: boolean }[] | undefined,
): boolean {
  const id = layer.id ?? "";
  const row = meta.get(id);
  if (row) return row.hasResults;
  if (!id.startsWith("target-points-")) return false;
  const setId = id.slice("target-points-".length);
  return !!events?.some(
    (ev) => ev.event_id === `points:${setId}` && ev.has_results,
  );
}

// Zone versions name their event on the sidebar meta. A saved points design
// does not: the manifest row carries `sample_event_id` (`points:<set_id>`).
export function targetServerEventId(
  layer: { id?: string; sample_event_id?: string | null },
  meta: ReadonlyMap<string, { eventId: string }>,
): string | undefined {
  const fromMeta = meta.get(layer.id ?? "")?.eventId;
  const raw = fromMeta ?? layer.sample_event_id;
  return raw ? raw : undefined;
}

export function targetDesignId(layerId: string): string | null {
  const matched = /^target-(?:points|zones)-(.+)$/.exec(layerId);
  return matched?.[1] ?? null;
}

// Every server row confirms before the request. A browser-only copy does not.
export function targetDeleteNeedsConfirm(
  layerId: string,
  serverEventId: string | undefined,
): boolean {
  return Boolean(serverEventId) || layerId === "zones";
}

// A points design saved from this browser is listed twice: its browser copy
// (`target-points-<client id>`) and the server row (`target-points-<set_id>`).
// The ids never match, so the copy is found by its points (any order).
function sortedPoints(
  fc: GeoJSON.FeatureCollection | null | undefined,
): [number, number][] | null {
  const out: [number, number][] = [];
  for (const f of fc?.features ?? []) {
    const c = (f.geometry as { coordinates?: unknown } | null | undefined)
      ?.coordinates;
    if (
      !Array.isArray(c) ||
      typeof c[0] !== "number" ||
      typeof c[1] !== "number"
    )
      return null;
    out.push([c[0], c[1]]);
  }
  if (!out.length) return null;
  return out.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
}

export function samePointSet(
  a: GeoJSON.FeatureCollection | null | undefined,
  b: GeoJSON.FeatureCollection | null | undefined,
): boolean {
  const pa = sortedPoints(a);
  const pb = sortedPoints(b);
  if (!pa || !pb || pa.length !== pb.length) return false;
  return pa.every(
    (p, i) =>
      Math.abs(p[0] - pb[i]![0]) <= 1e-7 && Math.abs(p[1] - pb[i]![1]) <= 1e-7,
  );
}

export function browserPointTwins(
  layer: Pick<ManifestLayer, "id" | "inline">,
  sets: readonly TargetSet[],
): string[] {
  const id = layer.id ?? "";
  if (!id.startsWith("target-points-")) return [];
  const fc = (layer.inline as GeoJSON.FeatureCollection | undefined) ?? null;
  return sets
    .filter(
      (s) =>
        s.kind === "points" &&
        `target-points-${s.id}` !== id &&
        samePointSet(s.fc, fc),
    )
    .map((s) => s.id);
}

export function targetDeleteErrorText(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return "Could not delete this layer";
}

// The browser copy is removed only after the server accepts the delete, so a
// failed request can put the row back. A refresh that fails afterwards must
// not paint the deleted design back into the list.
export async function commitTargetLayerDelete(args: {
  fieldId: string;
  layerId: string;
  serverEventId: string | undefined;
  // Browser copies of the same points design (browserPointTwins). They go
  // only after the server accepts the delete.
  twinIds?: readonly string[];
  deleteSampleEvent: (fieldId: string, eventId: string) => Promise<void>;
  deleteZones: (fieldId: string) => Promise<void>;
  removeLocal: (fieldId: string, designId: string) => void;
  invalidate: () => Promise<void>;
  onFailed: (message: string) => void;
}): Promise<void> {
  const server = targetDeleteNeedsConfirm(args.layerId, args.serverEventId);
  let deleted = false;
  try {
    if (args.serverEventId) {
      await args.deleteSampleEvent(args.fieldId, args.serverEventId);
    } else if (args.layerId === "zones") {
      await args.deleteZones(args.fieldId);
    }
    const designId = targetDesignId(args.layerId);
    if (designId) args.removeLocal(args.fieldId, designId);
    for (const twin of args.twinIds ?? []) args.removeLocal(args.fieldId, twin);
    if (!server) return;
    deleted = true;
    await args.invalidate();
  } catch (err) {
    if (deleted) {
      reportSwallowed("refresh after target delete", err);
      return;
    }
    args.onFailed(targetDeleteErrorText(err));
  }
}

/*
 *
 * The folder rows for a field's server zone versions.
 *
 * - Only versions whose geometry has LOADED become layers (a title-only row can't be toggled and
 * would flicker into a duplicate before `is_active` is known).
 * - The ACTIVE version is the field's `zones` manifest row already on the map (default-visible), so
 * when that row is present the active version emits NO second layer — its meta is keyed to `zones`
 * instead, and the folder shows the manifest row under the version's name. Without a manifest row
 * (older service, or the read raced) it lists like any other version.
 */
export function serverZoneLayers(
  events: ZoneVersionEvent[] | undefined,
  geomByVersion: ReadonlyMap<string, ZoneVersionGeom | undefined>,
  opts: { hasManifestZones: boolean },
): ServerZoneLayers {
  const layers: ManifestLayer[] = [];
  const meta = new Map<string, ServerZoneRow>();
  const fcByVersion = new Map<string, GeoJSON.FeatureCollection>();
  for (const ev of events ?? []) {
    const versionId = zoneVersionIdOf(ev.event_id);
    if (!versionId) continue;
    const g = geomByVersion.get(versionId);
    if (!g?.geojson) continue;
    fcByVersion.set(versionId, g.geojson);
    const isActive = !!g.is_active;
    const layerId =
      isActive && opts.hasManifestZones ? "zones" : `target-zones-${versionId}`;
    const row: ServerZoneRow = {
      layerId,
      versionId,
      eventId: ev.event_id,
      name: ev.name,
      count: ev.count || g.geojson.features.length,
      hasResults: ev.has_results,
      isActive,
    };
    meta.set(layerId, row);
    if (layerId === "zones") continue;
    layers.push({
      id: layerId,
      kind: "geojson",
      title: ev.name,
      inline: g.geojson,
      default_visible: false,
      default_opacity: 0.45,
      order: 205,
      content_version: ev.created_at,
      is_stale: false,
    });
  }
  return { layers, meta, fcByVersion };
}

/*
 *
 * Merge client-registered target layers with the server zone layers into ONE list: a client zone
 * layer that IS a server version (same id — registered with the version id — or the same geometry
 * for copies from before ids were shared) is dropped in favour of the server row.
 */
export function mergeTargetLayers(
  client: ManifestLayer[],
  server: ManifestLayer[],
  sameGeometry: (
    a: GeoJSON.FeatureCollection | null,
    b: GeoJSON.FeatureCollection | null,
  ) => boolean,
): ManifestLayer[] {
  const serverIds = new Set(server.map((l) => l.id));
  const kept = client.filter((l) => {
    if (!(l.id ?? "").startsWith("target-zones-")) return true;
    if (serverIds.has(l.id)) return false;
    const fc = (l.inline as GeoJSON.FeatureCollection | undefined) ?? null;
    return !server.some((s) =>
      sameGeometry(
        fc,
        (s.inline as GeoJSON.FeatureCollection | undefined) ?? null,
      ),
    );
  });
  return [...kept, ...server];
}
